import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_ORGANIZATION_MEMBERS,
  GET_ORGANIZATION_USAGE,
  INVITE_TO_ORGANIZATION,
  UPDATE_ORGANIZATION_MEMBER_ROLE,
  REMOVE_ORGANIZATION_MEMBER,
} from '../graphql/organizations';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '@clerk/clerk-react';

export function TeamManagementPage() {
  const { user } = useUser();
  const { currentOrganization } = useOrganization();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: membersData, loading: membersLoading, refetch: refetchMembers } = useQuery(
    GET_ORGANIZATION_MEMBERS,
    {
      variables: { organizationId: currentOrganization?.id },
      skip: !currentOrganization,
    }
  );

  const { data: usageData } = useQuery(GET_ORGANIZATION_USAGE, {
    variables: { organizationId: currentOrganization?.id },
    skip: !currentOrganization,
  });

  const [inviteMember, { loading: inviting }] = useMutation(INVITE_TO_ORGANIZATION, {
    onCompleted: (data) => {
      setSuccess(`Invitation sent to ${inviteEmail}!`);
      setInviteEmail('');
      setShowInviteForm(false);
      refetchMembers();
      // TODO: Show the invite link to the user
      console.log('Invite link:', data.inviteToOrganization.inviteLink);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const [updateRole] = useMutation(UPDATE_ORGANIZATION_MEMBER_ROLE, {
    onCompleted: () => {
      setSuccess('Member role updated successfully');
      refetchMembers();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const [removeMember] = useMutation(REMOVE_ORGANIZATION_MEMBER, {
    onCompleted: () => {
      setSuccess('Member removed successfully');
      refetchMembers();
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentOrganization) return;

    try {
      await inviteMember({
        variables: {
          input: {
            organizationId: currentOrganization.id,
            email: inviteEmail,
            role: inviteRole,
          },
        },
      });
    } catch (err) {
      // Handled by onError
    }
  };

  const handleRoleChange = async (memberId: string, userId: string, newRole: string) => {
    if (!currentOrganization) return;

    if (window.confirm(`Are you sure you want to change this member's role to ${newRole}?`)) {
      await updateRole({
        variables: {
          input: {
            organizationId: currentOrganization.id,
            userId,
            newRole,
          },
        },
      });
    }
  };

  const handleRemove = async (memberId: string, userId: string, email: string) => {
    if (!currentOrganization) return;

    if (window.confirm(`Are you sure you want to remove ${email} from this organization? This action cannot be undone.`)) {
      await removeMember({
        variables: {
          organizationId: currentOrganization.id,
          userId,
        },
      });
    }
  };

  if (!currentOrganization) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <p className="text-gray-500">Select an organization to manage team members.</p>
      </div>
    );
  }

  const members = membersData?.organizationMembers || [];
  const usage = usageData?.organizationUsage;
  const currentUserMember = members.find((m: any) => m.userId === user?.id);
  const isAdmin = currentUserMember?.role === 'admin';

  const canInvite = usage?.limits?.canInviteMembers && isAdmin;
  const atMemberLimit = usage && usage.limits.maxMembers !== -1 && usage.currentMembers >= usage.limits.maxMembers;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
        <p className="mt-2 text-gray-600">
          Manage members of <span className="font-semibold">{currentOrganization.name}</span>
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{success}</span>
          <button onClick={() => setSuccess('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Usage Stats */}
      {usage && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Team Usage</h3>
              <p className="text-sm text-blue-700 mt-1">
                {usage.currentMembers} / {usage.limits.maxMembers === -1 ? 'Unlimited' : usage.limits.maxMembers} members
              </p>
            </div>
            {usage.memberUsagePercent > 80 && usage.limits.maxMembers !== -1 && (
              <div className="text-sm text-blue-700">
                ⚠️ {Math.round(usage.memberUsagePercent)}% of member limit used
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Form */}
      {!isAdmin && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            Only organization admins can invite new members.
          </p>
        </div>
      )}

      {canInvite && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
          {!showInviteForm ? (
            <button
              onClick={() => setShowInviteForm(true)}
              disabled={atMemberLimit}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Invite Team Member
            </button>
          ) : (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="teammate@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    id="role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as any)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteEmail('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Members List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <ul className="divide-y divide-gray-200">
          {membersLoading ? (
            <li className="p-6 text-center text-gray-500">Loading members...</li>
          ) : members.length === 0 ? (
            <li className="p-6 text-center text-gray-500">No members yet</li>
          ) : (
            members.map((member: any) => (
              <li key={member.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-gray-600 font-medium text-sm">
                            {member.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex-1">
                        <p className="text-sm font-medium text-gray-900">{member.email}</p>
                        <div className="flex items-center mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            member.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            member.role === 'member' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {member.role}
                          </span>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            member.status === 'active' ? 'bg-green-100 text-green-800' :
                            member.status === 'invited' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {member.status}
                          </span>
                          {member.userId === user?.id && (
                            <span className="ml-2 text-xs text-gray-500">(You)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {isAdmin && member.userId !== user?.id && (
                    <div className="ml-4 flex items-center space-x-2">
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, member.userId, e.target.value)}
                        className="text-sm border-gray-300 rounded-md"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        onClick={() => handleRemove(member.id, member.userId, member.email)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Role Descriptions */}
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Role Permissions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <h4 className="font-medium text-gray-900">Admin</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Full organization access</li>
              <li>• Invite & manage members</li>
              <li>• Create & delete projects</li>
              <li>• Manage billing</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Member</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Create projects</li>
              <li>• Create & edit contexts</li>
              <li>• Access assigned projects</li>
              <li>• Cannot manage members</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Viewer</h4>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Read-only access</li>
              <li>• View assigned projects</li>
              <li>• Cannot create or edit</li>
              <li>• Cannot manage members</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
